const CONSENT_COOKIE_NAME = 'cc_cookie';
const CONSENT_COOKIE_EXPIRATION_DAYS = 182;
const CONSENT_COOKIE_PATH = '/';
const CONSENT_COOKIE_SAME_SITE = 'Lax';
const CONSENT_REVISION = 0;
export const NECESSARY_CATEGORY = 'necessary';
export const ANALYTICS_CATEGORY = 'analytics';
export const TARGETING_CATEGORY = 'targeting';
export const CONSENT_CATEGORIES = [
    NECESSARY_CATEGORY,
    ANALYTICS_CATEGORY,
    TARGETING_CATEGORY
];
const READONLY_CATEGORIES = [NECESSARY_CATEGORY];
const createConsentUuid = () => {
    if (typeof crypto !== 'undefined' &&
        typeof crypto.randomUUID === 'function') {
        return crypto.randomUUID();
    }
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, character => {
        const random = Math.floor(Math.random() * 16);
        return (character === 'x' ? random : (random & 0x3) | 0x8).toString(16);
    });
};
export const readConsentCookie = () => {
    if (typeof document === 'undefined')
        return null;
    const match = document.cookie.match(`(^|;)\\s*${CONSENT_COOKIE_NAME}\\s*=\\s*([^;]+)`);
    const raw = match?.[2];
    if (!raw)
        return null;
    try {
        return JSON.parse(raw);
    }
    catch {
        try {
            return JSON.parse(decodeURIComponent(raw));
        }
        catch {
            return null;
        }
    }
};
export const isConsentValid = (cookie) => {
    if (!cookie)
        return false;
    const revision = typeof cookie.revision === 'number' ? cookie.revision : CONSENT_REVISION;
    return (revision === CONSENT_REVISION &&
        Boolean(cookie.consent_uuid) &&
        Boolean(cookie.consent_date) &&
        Boolean(cookie.last_consent_update));
};
export const hasValidConsent = () => isConsentValid(readConsentCookie());
export const readAllowedCategories = () => {
    const cookie = readConsentCookie();
    return isConsentValid(cookie) ? (cookie.categories ?? []) : [];
};
export const resolveAcceptedCategories = (categories, exclusions) => {
    let accepted;
    if (typeof categories === 'string') {
        accepted =
            categories === 'all'
                ? [...CONSENT_CATEGORIES]
                : CONSENT_CATEGORIES.includes(categories)
                    ? [categories]
                    : [];
    }
    else if (Array.isArray(categories)) {
        accepted = categories.filter(category => CONSENT_CATEGORIES.includes(category));
    }
    else {
        accepted = readAllowedCategories();
    }
    if (exclusions && exclusions.length > 0) {
        accepted = accepted.filter(category => !exclusions.includes(category));
    }
    for (const category of READONLY_CATEGORIES) {
        if (!accepted.includes(category))
            accepted.push(category);
    }
    return accepted;
};
const writeCookie = (value) => {
    const expires = new Date(Date.now() + CONSENT_COOKIE_EXPIRATION_DAYS * 24 * 60 * 60 * 1000);
    let cookie = `${CONSENT_COOKIE_NAME}=${encodeURIComponent(value)}` +
        `; expires=${expires.toUTCString()}` +
        `; Path=${CONSENT_COOKIE_PATH};`;
    cookie += ` SameSite=${CONSENT_COOKIE_SAME_SITE};`;
    if (location.hostname.indexOf('.') > -1) {
        cookie += ` Domain=${location.hostname};`;
    }
    if (location.protocol === 'https:')
        cookie += ' Secure;';
    document.cookie = cookie;
};
export const writeConsent = (categories) => {
    const previous = readConsentCookie();
    const now = new Date().toISOString();
    const cookie = {
        categories,
        level: categories,
        revision: CONSENT_REVISION,
        data: previous?.data ?? null,
        rfc_cookie: false,
        consent_date: previous?.consent_date ?? now,
        consent_uuid: previous?.consent_uuid ?? createConsentUuid(),
        last_consent_update: now
    };
    writeCookie(JSON.stringify(cookie));
    return cookie;
};
export const diffCategories = (previous, next) => {
    const changed = previous.filter(category => !next.includes(category));
    for (const category of next) {
        if (!previous.includes(category))
            changed.push(category);
    }
    return changed;
};
export const eraseCookies = (names, path, domains) => {
    if (typeof document === 'undefined')
        return;
    const targets = domains ?? [location.hostname, `.${location.hostname}`];
    for (const name of names) {
        for (const domain of targets) {
            document.cookie =
                `${name}=; path=${path ?? CONSENT_COOKIE_PATH}` +
                    `; domain=${domain}; Expires=Thu, 01 Jan 1970 00:00:01 GMT;`;
        }
    }
};
